import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import get from 'lodash/get'

import { getPermission, PERMISSION_STATES, PERMISSIONS } from '../../utils/permissions-service'
import { getGeolocation } from '../../utils/geolocation'
import { onClearGeolocation, selectGeolocation } from '../../root/reducers/shipments/geolocation'

const DEFAULT_COORDS = {
  ng: '6.5244,3.3792', // Lagos, Nigeria
  ke: '-1.2921,36.8219' // Nairobi, Kenya
}

const onGetGeolocation = async ({ permission, dispatch, watchId, callback = null, maximumAge = 5 }) => {
  const configGeolocationOptions = { maximumAge } // default to 5 seconds

  // We need to pass dispatch in order to update the redux store
  try {
    const result = await getGeolocation(permission, dispatch, configGeolocationOptions)

    // Call callback after initial permission is handled (before starting watch)
    // This ensures the UI doesn't hang while waiting for watchPosition
    if (permission === PERMISSION_STATES.PROMPT && typeof callback === 'function' && !watchId) {
      callback()
    }

    // Start watching position if permission is granted after initial prompt
    // Don't await - watchPosition never resolves as it's a continuous watcher
    if (result.newPermission === PERMISSION_STATES.GRANTED && result.newPermission !== permission) {
      getGeolocation(result.newPermission, dispatch, configGeolocationOptions)
    }
  } catch (error) {
    console.warn(error)
    // Still call callback on error so the UI doesn't hang
    if (permission === PERMISSION_STATES.PROMPT && typeof callback === 'function' && !watchId) {
      callback()
    }
  }
}

const useGeolocation = (api, shipment) => {
  const dispatch = useDispatch()
  const geolocationData = useSelector(selectGeolocation)

  const [locationObject, setLocationObject] = useState()
  const [distance, setDistance] = useState()
  const [loading, setLoading] = useState(true)

  const { position, watchId } = geolocationData

  const onGetCurrentDistance = async () => {
    // Using this as a fallback since some test shipments lack a location object
    // (locationObject = undefined) or have invalid latLng values ('null,null').
    const country = get(locationObject, 'location.country', DEFAULT_COORDS.ng)
    const facilityCoordsMock = DEFAULT_COORDS[country]

    const facilityPosition = get(locationObject, 'additionalData.latLng', facilityCoordsMock)
    const [latPositionFacility, lngPositionFacility] = (
      facilityPosition === 'null,null' ? facilityCoordsMock.split(',') : facilityPosition.split(',')
    )

    try {
      const distance = await api.shipment.calculateDistance(
        { lat: position.lat, long: position.lng }, // from point A coordinates
        { lat: Number(latPositionFacility), long: Number(lngPositionFacility) } // to point B coordinates
      )
      setDistance(distance)
    } catch (error) {
      console.warn(error)
    } finally {
      setLoading(false)
    }
  }

  const onHandleGetCurrentDistance = async () => {
    if (position.lat && position.lng && locationObject) {
      await onGetCurrentDistance()
    } else {
      setLoading(false)
    }
  }

  const onHandleGetGeolocation = async () => {
    const locationId = get(shipment, 'destination.id', '')
    let permission

    try {
      let locationObject = await api.location.get(locationId)
      if (!locationObject) {
        // fetch from agave
        const locations = await api.location.getLocationsViaIds([locationId])
        locationObject = locations[0]
      }
      permission = await getPermission(PERMISSIONS.GEOLOCATION)
      setLocationObject(locationObject)

      if (permission && permission !== PERMISSION_STATES.PROMPT) {
        await onGetGeolocation({ permission, dispatch, watchId })
      }
    } catch (error) {
      console.warn(error)
    } finally {
      if (permission === PERMISSION_STATES.PROMPT) {
        setLoading(false)
      }
    }
  }

  const onClearGeolocationWatch = () => {
    if (watchId) {
      dispatch(onClearGeolocation(watchId))
    }
  }

  useEffect(() => {
    onHandleGetGeolocation()
  }, [])

  useEffect(() => {
    onHandleGetCurrentDistance()
    return () => onClearGeolocationWatch()
  }, [position.lat, position.lng, locationObject, watchId])

  return {
    locationObject,
    geolocationData,
    distance,
    loading
  }
}

export {
  useGeolocation,
  onGetGeolocation
}
